import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { List as VirtualList } from "react-window";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";

import { useLazyGetHelperSyncTokenQuery } from "../toolkitServices/userApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetCountersAllQuery } from "../toolkitServices/counterApi";

import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";

import { handleDropDowns } from "../config/dropDownHelpers";

import {
  FaWifi,
  FaPlug,
  FaPlay,
  FaStop,
  FaTrashAlt,
  FaSearch,
  FaMobileAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBoxOpen,
  FaInfoCircle,
  FaCrosshairs,
  FaPlus,
  FaTimes,
} from "react-icons/fa";
import { FiRotateCcw, FiRefreshCw } from "react-icons/fi";

/* ---------------- animation ---------------- */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.42, ease: "easeOut" },
  },
});

/* ---------------- helpers ---------------- */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const normalizeText = (value) => String(value || "").trim();
const normalizeUpper = (value) => normalizeText(value).toUpperCase();

const toStatusClass = (status) => {
  switch (status) {
    case "matched":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "not_found":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const getSignalTheme = (level = "", strength = 0) => {
  const safe = Math.max(0, Math.min(100, Number(strength) || 0));
  const normalizedLevel = String(level || "").toLowerCase();

  if (safe >= 70 || ["very_close", "close"].includes(normalizedLevel)) {
    return {
      wrap: "border-emerald-300 bg-emerald-50",
      chip: "bg-emerald-100 text-emerald-700 border-emerald-200",
      glow: "shadow-[0_0_0_6px_rgba(16,185,129,0.12)]",
      text: "text-emerald-700",
    };
  }

  if (safe >= 35 || ["near"].includes(normalizedLevel)) {
    return {
      wrap: "border-orange-300 bg-orange-50",
      chip: "bg-orange-100 text-orange-700 border-orange-200",
      glow: "shadow-[0_0_0_6px_rgba(249,115,22,0.14)]",
      text: "text-orange-700",
    };
  }

  if (safe > 0 || ["far", "detected", "searching"].includes(normalizedLevel)) {
    return {
      wrap: "border-rose-300 bg-rose-50",
      chip: "bg-rose-100 text-rose-700 border-rose-200",
      glow: "shadow-[0_0_0_6px_rgba(244,63,94,0.12)]",
      text: "text-rose-700",
    };
  }

  return {
    wrap: "border-slate-200 bg-white",
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    glow: "",
    text: "text-slate-700",
  };
};

const getSignalLabel = (strength = 0, level = "", fallback = "idle") => {
  const safe = Math.max(0, Math.min(100, Number(strength) || 0));
  const normalizedLevel = String(level || "").toLowerCase();

  if (safe >= 70 || normalizedLevel === "very_close") return "very near";
  if (safe >= 35 || normalizedLevel === "near") return "getting closer";
  if (safe > 0 || normalizedLevel === "far") return "far";
  if (normalizedLevel === "searching") return "searching";
  return fallback || "idle";
};

const getSignalMessage = (strength = 0, fallback = "") => {
  const safe = Math.max(0, Math.min(100, Number(strength) || 0));
  if (safe >= 70) return "Tag is very close";
  if (safe >= 35) return "Getting closer";
  if (safe > 0) return "Tag is far";
  return fallback || "Searching for selected item";
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 16,
    borderColor: state.isFocused ? "#c4b5fd" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(196,181,253,0.35)" : "none",
    "&:hover": { borderColor: "#c4b5fd" },
    backgroundColor: "#f8fafc",
    fontSize: "12px",
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#ede9fe",
    borderRadius: 9999,
    paddingInline: 2,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#5b21b6",
    fontSize: "11px",
    fontWeight: 700,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#5b21b6",
    ":hover": {
      backgroundColor: "#ddd6fe",
      color: "#4c1d95",
    },
  }),
  singleValue: (base) => ({ ...base, color: "#0f172a" }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontSize: "12px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#0f172a",
  }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  option: (base, state) => ({
    ...base,
    fontSize: "12px",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 20000 }),
  menuPortal: (base) => ({ ...base, zIndex: 20000 }),
};

const SummaryChip = ({ title, value, tone = "slate", icon }) => {
  const toneClass = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };

  return (
    <div className={`rounded-2xl border p-3 shadow-sm ${toneClass[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide font-extrabold opacity-80">
            {title}
          </div>
          <div className="mt-1 text-xl sm:text-2xl font-extrabold leading-none">
            {value}
          </div>
        </div>
        <div className="text-lg opacity-75">{icon}</div>
      </div>
    </div>
  );
};

const EmptyState = ({ icon, title, subtitle }) => (
  <div className="rounded-2xl border bg-slate-50 p-4 text-center">
    <div className="mx-auto mb-2 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white border shadow-sm text-slate-700">
      {icon}
    </div>
    <div className="text-sm font-extrabold text-slate-800">{title}</div>
    <div className="text-[11px] text-slate-500 mt-1">{subtitle}</div>
  </div>
);

const ListLoadingPill = ({ show }) => (
  <AnimatePresence>
    {show ? (
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm"
      >
        <span className="relative inline-flex h-4 w-4 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-slate-300/60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-slate-400" />
        </span>
        Loading stock…
      </motion.div>
    ) : null}
  </AnimatePresence>
);

const countSelected = (arr) => (Array.isArray(arr) ? arr.length : 0);

const ManualEpcModal = ({
  open,
  onClose,
  manualEpc,
  setManualEpc,
  onAddManualEpc,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10010] bg-black/45 backdrop-blur-sm flex items-center justify-center p-3">
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="px-4 py-3 border-b bg-gradient-to-r from-violet-600 via-violet-500 to-indigo-500 text-white flex items-center justify-between">
          <div>
            <div className="text-[13px] font-extrabold">Add EPC Manually</div>
            <div className="text-[11px] text-white/90 mt-0.5">
              Manual EPC add yahin se hoga
            </div>
          </div>

          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-white/15 border border-white/20 text-white flex items-center justify-center"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-4">
          <label className="block text-[11px] font-semibold text-slate-600 mb-1">
            EPC Code
          </label>
          <input
            autoFocus
            type="text"
            value={manualEpc}
            onChange={(e) => setManualEpc(e.target.value)}
            placeholder="Enter EPC"
            className="w-full px-4 py-3 rounded-2xl text-[13px] text-slate-800 border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300"
          />

          <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-end">
            <button
              onClick={onClose}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-4 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>

            <button
              onClick={onAddManualEpc}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-2xl sm:rounded-full bg-violet-600 px-4 py-2 text-[11px] font-semibold text-white hover:bg-violet-700"
            >
              Add EPC
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const SignalRadar = ({ strength = 0, level = "" }) => {
  const safe = Math.max(0, Math.min(100, Number(strength) || 0));
  const theme = getSignalTheme(level, safe);

  const tone =
    safe >= 70
      ? "emerald"
      : safe >= 35
        ? "orange"
        : safe > 0
          ? "rose"
          : "slate";

  const toneClasses = {
    emerald: {
      ring: "border-emerald-200",
      glow: "bg-emerald-400/15",
      center: "bg-emerald-500",
      core: "bg-emerald-100 border-emerald-300",
    },
    orange: {
      ring: "border-orange-200",
      glow: "bg-orange-400/15",
      center: "bg-orange-500",
      core: "bg-orange-100 border-orange-300",
    },
    rose: {
      ring: "border-rose-200",
      glow: "bg-rose-400/15",
      center: "bg-rose-500",
      core: "bg-rose-100 border-rose-300",
    },
    slate: {
      ring: "border-slate-200",
      glow: "bg-slate-300/20",
      center: "bg-slate-400",
      core: "bg-slate-100 border-slate-300",
    },
  };

  const c = toneClasses[tone];

  return (
    <div className="relative mx-auto h-36 w-36 sm:h-40 sm:w-40">
      <motion.div
        animate={
          safe > 0
            ? { scale: [0.92, 1.06, 0.92], opacity: [0.18, 0.32, 0.18] }
            : { scale: 1, opacity: 0.12 }
        }
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className={`absolute inset-0 rounded-full ${c.glow}`}
      />

      <div className={`absolute inset-0 rounded-full border ${c.ring}`} />
      <div className={`absolute inset-4 rounded-full border ${c.ring}`} />
      <div className={`absolute inset-8 rounded-full border ${c.ring}`} />
      <div className={`absolute inset-12 rounded-full border ${c.ring}`} />

      <div className="absolute left-1/2 top-2 bottom-2 w-px -translate-x-1/2 bg-slate-200" />
      <div className="absolute top-1/2 left-2 right-2 h-px -translate-y-1/2 bg-slate-200" />

      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
        className="absolute inset-2 rounded-full"
        style={{ transformOrigin: "50% 50%" }}
      >
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              safe >= 70
                ? "conic-gradient(from 0deg, rgba(16,185,129,0.42), rgba(16,185,129,0.18) 14%, rgba(16,185,129,0.08) 24%, transparent 38%)"
                : safe >= 35
                  ? "conic-gradient(from 0deg, rgba(249,115,22,0.42), rgba(249,115,22,0.18) 14%, rgba(249,115,22,0.08) 24%, transparent 38%)"
                  : safe > 0
                    ? "conic-gradient(from 0deg, rgba(244,63,94,0.42), rgba(244,63,94,0.18) 14%, rgba(244,63,94,0.08) 24%, transparent 38%)"
                    : "conic-gradient(from 0deg, rgba(148,163,184,0.30), rgba(148,163,184,0.14) 14%, rgba(148,163,184,0.06) 24%, transparent 38%)",
            clipPath: "circle(50% at 50% 50%)",
            filter: "blur(0.4px)",
          }}
        />
      </motion.div>

      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          animate={safe > 0 ? { scale: [1, 1.08, 1] } : { scale: 1 }}
          transition={{ duration: 0.9, repeat: Infinity }}
          className={`flex h-16 w-16 items-center justify-center rounded-full border-2 shadow-sm ${c.core}`}
        >
          <motion.div
            animate={
              safe > 0
                ? { scale: [1, 1.22, 1], opacity: [0.8, 1, 0.8] }
                : { scale: 1, opacity: 0.6 }
            }
            transition={{ duration: 0.8, repeat: Infinity }}
            className={`h-5 w-5 rounded-full ${c.center}`}
          />
        </motion.div>
      </div>

      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
        <div
          className={`rounded-full border bg-white px-3 py-1 text-[11px] font-extrabold shadow-sm ${theme.chip}`}
        >
          {safe}%
        </div>
      </div>
    </div>
  );
};

const FindItemsModal = ({
  open,
  onClose,
  selectedCount,
  targetFindEpc,
  findStatus,
  findMessage,
  findLevel,
  findStrength,
  foundAt,
  remainingQueue,
  isFindMode,
  stopFindItem,
  handleFindSelected,
  onConfirmFound,
}) => {
  if (!open) return null;

  const theme = getSignalTheme(findLevel, findStrength);
  const canConfirm = !!targetFindEpc;
  const signalLabel = getSignalLabel(findStrength, findLevel, findStatus);
  const signalMessage = getSignalMessage(findStrength, findMessage);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[10020] bg-black/45 backdrop-blur-sm flex items-center justify-center p-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 14, scale: 0.985 }}
          className="relative w-full max-w-[860px] rounded-[24px] bg-white border border-slate-200 shadow-2xl overflow-hidden"
        >
          <button
            onClick={onClose}
            className="absolute right-3 top-3 z-10 h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <FaTimes />
          </button>

          <div className="p-3 sm:p-4 bg-[linear-gradient(180deg,#fafafe_0%,#ffffff_100%)]">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-3 items-stretch">
              <div
                className={`rounded-[20px] border bg-white p-3 sm:p-4 ${theme.wrap} ${theme.glow} min-w-0`}
              >
                <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[10px] font-extrabold ${theme.chip}`}
                  >
                    <FaCrosshairs className="text-[9px]" />
                    {isFindMode ? "TRACKING" : "IDLE"}
                  </span>

                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold text-slate-600">
                    Remaining: {remainingQueue}
                  </span>
                </div>

                <div className="text-center mb-2">
                  <div
                    className={`text-sm font-extrabold capitalize ${theme.text}`}
                  >
                    {signalLabel}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500 font-medium">
                    {signalMessage}
                  </div>
                </div>

                <div className="px-1">
                  <SignalRadar strength={findStrength} level={findLevel} />
                </div>

                <div className="mt-6 grid grid-cols-3 gap-2">
                  <div className="rounded-2xl border bg-rose-50 px-2 py-2 text-center border-rose-200">
                    <div className="text-[10px] text-rose-500 font-semibold">
                      0-34
                    </div>
                    <div className="mt-1 text-[10px] font-bold text-rose-700">
                      Far
                    </div>
                  </div>
                  <div className="rounded-2xl border bg-orange-50 px-2 py-2 text-center border-orange-200">
                    <div className="text-[10px] text-orange-500 font-semibold">
                      35-69
                    </div>
                    <div className="mt-1 text-[10px] font-bold text-orange-700">
                      Closer
                    </div>
                  </div>
                  <div className="rounded-2xl border bg-emerald-50 px-2 py-2 text-center border-emerald-200">
                    <div className="text-[10px] text-emerald-500 font-semibold">
                      70-100
                    </div>
                    <div className="mt-1 text-[10px] font-bold text-emerald-700">
                      Near
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-[20px] border border-slate-200 bg-white p-3 sm:p-4 shadow-sm min-w-0">
                <div className="space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border bg-slate-50 px-3 py-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        Selected
                      </div>
                      <div className="mt-1 text-[13px] font-extrabold text-slate-800">
                        {selectedCount}
                      </div>
                    </div>

                    <div className="rounded-2xl border bg-slate-50 px-3 py-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        Last Signal
                      </div>
                      <div className="mt-1 text-[13px] font-extrabold text-slate-800 truncate">
                        {foundAt || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 pt-1">
                    <button
                      onClick={isFindMode ? stopFindItem : handleFindSelected}
                      disabled={!isFindMode && !selectedCount}
                      className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-[12px] font-semibold transition ${
                        isFindMode
                          ? "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                          : "bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                      }`}
                    >
                      {isFindMode ? <FaStop /> : <FaCrosshairs />}
                      {isFindMode ? "Stop Find" : "Start Finding"}
                    </button>

                    <button
                      onClick={onConfirmFound}
                      disabled={!canConfirm}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-[12px] font-semibold text-white disabled:opacity-50 hover:bg-emerald-700"
                    >
                      <FaCheckCircle />
                      Confirm Found
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const SyncFilterModal = ({
  open,
  onClose,
  onReset,
  onSync,
  syncing,
  filters,
  branchOptions,
  counterOptions,
  categoryOptions,
  productOptions,
  designOptions,
  purityOptions,
  selectedCategoryValues,
  selectedProductValues,
  selectedDesignValues,
  selectedPurityValues,
  onBranchChange,
  onCounterChange,
  onCategoryChange,
  onProductChange,
  onDesignChange,
  onPurityChange,
}) => {
  if (!open) return null;

  const appliedCount =
    countSelected(filters.branchIds) +
    countSelected(filters.counterIds) +
    countSelected(filters.categoryIds) +
    countSelected(filters.productIds) +
    countSelected(filters.designIds) +
    countSelected(filters.purityIds);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[10030] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          className="w-full sm:max-w-4xl h-[90vh] sm:h-auto sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
        >
          <div className="px-4 py-3 border-b bg-gradient-to-r from-violet-600 via-violet-500 to-indigo-500 text-white flex items-center justify-between">
            <div>
              <div className="text-[14px] font-extrabold">
                Sync Stock Filters
              </div>
              <div className="text-[11px] text-white/90 mt-0.5">
                Multiple select kar sakte ho. Sync only selected stock ke liye
                hoga.
              </div>
            </div>

            <button
              onClick={onClose}
              className="h-9 w-9 rounded-full bg-white/15 border border-white/20 text-white flex items-center justify-center"
            >
              <FaTimes />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="text-[12px] font-semibold text-slate-700">
                Selected filters:{" "}
                <span className="text-violet-700 font-extrabold">
                  {appliedCount}
                </span>
              </div>

              <button
                type="button"
                onClick={onReset}
                className="inline-flex items-center gap-2 rounded-full bg-white text-slate-700 px-3 py-2 text-[11px] font-semibold border border-slate-200 hover:bg-slate-50"
              >
                <FiRefreshCw />
                Reset
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block mb-1 text-[11px] text-slate-600">
                  Branch
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  styles={selectStyles}
                  options={branchOptions}
                  value={filters.branchIds}
                  onChange={onBranchChange}
                  placeholder="Select branches"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPlacement="auto"
                />
              </div>

              <div>
                <label className="block mb-1 text-[11px] text-slate-600">
                  Counter
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  styles={selectStyles}
                  options={counterOptions}
                  value={filters.counterIds}
                  onChange={onCounterChange}
                  placeholder={
                    filters.branchIds.length
                      ? "Select counters"
                      : "Select branch first"
                  }
                  isDisabled={!filters.branchIds.length}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPlacement="auto"
                />
              </div>

              <div>
                <label className="block mb-1 text-[11px] text-slate-600">
                  Category
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  styles={selectStyles}
                  options={categoryOptions}
                  value={selectedCategoryValues}
                  onChange={onCategoryChange}
                  placeholder="Select categories"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPlacement="auto"
                />
              </div>

              <div>
                <label className="block mb-1 text-[11px] text-slate-600">
                  Product
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  styles={selectStyles}
                  options={productOptions}
                  value={selectedProductValues}
                  onChange={onProductChange}
                  placeholder={
                    filters.categoryIds.length
                      ? "Select products"
                      : "Select category first"
                  }
                  isDisabled={!filters.categoryIds.length}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPlacement="auto"
                />
              </div>

              <div>
                <label className="block mb-1 text-[11px] text-slate-600">
                  Design
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  styles={selectStyles}
                  options={designOptions}
                  value={selectedDesignValues}
                  onChange={onDesignChange}
                  placeholder={
                    filters.productIds.length
                      ? "Select designs"
                      : "Select product first"
                  }
                  isDisabled={!filters.productIds.length}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPlacement="auto"
                />
              </div>

              <div>
                <label className="block mb-1 text-[11px] text-slate-600">
                  Purity
                </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  styles={selectStyles}
                  options={purityOptions}
                  value={selectedPurityValues}
                  onChange={onPurityChange}
                  placeholder="Select purities"
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPlacement="auto"
                />
              </div>
            </div>
          </div>

          <div className="border-t bg-white px-4 py-3 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-4 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onSync}
              disabled={syncing}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-violet-600 px-4 py-2 text-[11px] font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {syncing ? <Spinner size={14} /> : <FaBoxOpen />}
              {syncing ? "Syncing..." : "Sync"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ---------------- main page ---------------- */
export default function StockValidation() {
  const helperAppUrl = `${process.env.REACT_APP_API_BASE_URL}/downloads/uhf-helper.apk`;

  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const isFindModeRef = useRef(false);

  const [getHelperSyncToken] = useLazyGetHelperSyncTokenQuery();

  const [helperStatus, setHelperStatus] = useState("Connecting...");
  const [readerStatus, setReaderStatus] = useState("Not Initialized");
  const [search, setSearch] = useState("");
  const [manualEpc, setManualEpc] = useState("");
  const [statusFilter, setStatusFilter] = useState("not_found");
  const [lastEpc, setLastEpc] = useState("");
  const [isReaderInitialized, setIsReaderInitialized] = useState(false);
  const [isExpectedLoaded, setIsExpectedLoaded] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [listHeight, setListHeight] = useState(600);
  const [rows, setRows] = useState([]);

  const [isFindMode, setIsFindMode] = useState(false);
  const [targetFindEpc, setTargetFindEpc] = useState("");
  const [targetFindItem, setTargetFindItem] = useState(null);
  const [findStatus, setFindStatus] = useState("idle");
  const [findMessage, setFindMessage] = useState("No active search");
  const [findLevel, setFindLevel] = useState("");
  const [findStrength, setFindStrength] = useState(0);
  const [foundAt, setFoundAt] = useState("");
  const [helperSummary, setHelperSummary] = useState(null);

  const [showManualModal, setShowManualModal] = useState(false);
  const [showFindModal, setShowFindModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  const [selectedEpcs, setSelectedEpcs] = useState([]);
  const [findQueue, setFindQueue] = useState([]);

  const [syncProgress, setSyncProgress] = useState({
    syncing: false,
    total: 0,
    synced: 0,
    percent: 0,
  });

  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listHasMore, setListHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const [filters, setFilters] = useState({
    branchIds: [],
    counterIds: [],
    categoryIds: [],
    productIds: [],
    designIds: [],
    purityIds: [],
  });

  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();
  const { data: designs } = useGetAllDesignsQuery();
  const { data: purities } = useGetAllPuritiesQuery();

  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length) dispatch(setProductList(products.data));
    if (designs?.data?.length) dispatch(setDesignList(designs.data));
    if (purities?.data?.length) dispatch(setPurityList(purities.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
  }, [dispatch, branches, categories, products, designs, purities]);

  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);

  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState || [] }),
    [branchesFromState],
  );

  const categoryOptions = useMemo(
    () =>
      handleDropDowns({
        key: "category",
        categories: categoriesFromState || [],
      }),
    [categoriesFromState],
  );

  const productOptions = useMemo(() => {
    const selectedCategoryIds = filters.categoryIds.map((x) => String(x.value));

    const opts = handleDropDowns({
      key: "product",
      products: productsFromState || [],
      category: null,
    });

    if (!selectedCategoryIds.length) return opts;

    return opts.filter((opt) => {
      const raw = (productsFromState || []).find(
        (p) => String(p.Id ?? p.ProductId ?? p.value) === String(opt.value),
      );
      return selectedCategoryIds.includes(
        String(raw?.CategoryId ?? raw?.categoryId ?? ""),
      );
    });
  }, [productsFromState, filters.categoryIds]);

  const designOptions = useMemo(() => {
    const selectedProductIds = filters.productIds.map((x) => String(x.value));

    const opts = handleDropDowns({
      key: "design",
      designs: designFromState || [],
      product: null,
    });

    if (!selectedProductIds.length) return opts;

    return opts.filter((opt) => {
      const raw = (designFromState || []).find(
        (d) => String(d.Id ?? d.DesignId ?? d.value) === String(opt.value),
      );
      return selectedProductIds.includes(
        String(raw?.ProductId ?? raw?.productId ?? ""),
      );
    });
  }, [designFromState, filters.productIds]);

  const purityOptions = useMemo(() => {
    const selectedCategoryIds = filters.categoryIds.map((x) => String(x.value));

    const opts = handleDropDowns({
      key: "purity",
      purities: purityFromState || [],
      category: null,
    });

    if (!selectedCategoryIds.length) return opts;

    return opts.filter((opt) => {
      const raw = (purityFromState || []).find(
        (p) => String(p.Id ?? p.PurityId ?? p.value) === String(opt.value),
      );
      return selectedCategoryIds.includes(
        String(raw?.CategoryId ?? raw?.categoryId ?? ""),
      );
    });
  }, [purityFromState, filters.categoryIds]);

  const selectedCategoryValues = useMemo(
    () =>
      categoryOptions.filter((o) =>
        filters.categoryIds.some((x) => String(x.value) === String(o.value)),
      ),
    [categoryOptions, filters.categoryIds],
  );

  const selectedProductValues = useMemo(
    () =>
      productOptions.filter((o) =>
        filters.productIds.some((x) => String(x.value) === String(o.value)),
      ),
    [productOptions, filters.productIds],
  );

  const selectedDesignValues = useMemo(
    () =>
      designOptions.filter((o) =>
        filters.designIds.some((x) => String(x.value) === String(o.value)),
      ),
    [designOptions, filters.designIds],
  );

  const selectedPurityValues = useMemo(
    () =>
      purityOptions.filter((o) =>
        filters.purityIds.some((x) => String(x.value) === String(o.value)),
      ),
    [purityOptions, filters.purityIds],
  );

  const { data: countersRes } = useGetCountersAllQuery(
    {
      page: 1,
      limit: 500,
      branchId: filters.branchIds[0]?.value || "",
      active: true,
    },
    { skip: !filters.branchIds.length },
  );

  const counterOptions = useMemo(() => {
    const list = countersRes?.data || [];
    return (Array.isArray(list) ? list : []).map((c) => ({
      value: c?._id,
      label: c?.counterName || c?.name || "Counter",
    }));
  }, [countersRes]);

  useEffect(() => {
    connectHelper();
    return () => {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) {}
      }
    };
  }, []);

  useEffect(() => {
    isFindModeRef.current = isFindMode;
  }, [isFindMode]);

  useEffect(() => {
    const updateHeight = () => {
      if (window.innerWidth < 768) {
        setListHeight(window.innerHeight - 210);
      } else {
        setListHeight(window.innerHeight - 220);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const resetFindUi = () => {
    setIsFindMode(false);
    setTargetFindEpc("");
    setTargetFindItem(null);
    setFindStatus("idle");
    setFindMessage("No active search");
    setFindLevel("");
    setFindStrength(0);
    setFoundAt("");
  };

  const resetFilters = () => {
    setFilters({
      branchIds: [],
      counterIds: [],
      categoryIds: [],
      productIds: [],
      designIds: [],
      purityIds: [],
    });
  };

  const onBranchChange = (opts) => {
    setFilters((prev) => ({
      ...prev,
      branchIds: opts || [],
      counterIds: [],
    }));
  };

  const onCounterChange = (opts) => {
    setFilters((prev) => ({
      ...prev,
      counterIds: opts || [],
    }));
  };

  const onCategoryChange = (opts) => {
    setFilters((prev) => ({
      ...prev,
      categoryIds: opts || [],
      productIds: [],
      designIds: [],
      purityIds: [],
    }));
  };

  const onProductChange = (opts) => {
    setFilters((prev) => ({
      ...prev,
      productIds: opts || [],
      designIds: [],
    }));
  };

  const onDesignChange = (opts) => {
    setFilters((prev) => ({
      ...prev,
      designIds: opts || [],
    }));
  };

  const onPurityChange = (opts) => {
    setFilters((prev) => ({
      ...prev,
      purityIds: opts || [],
    }));
  };

  const connectHelper = () => {
    try {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) {}
      }

      setHelperStatus("Connecting...");

      const ws = new WebSocket("ws://127.0.0.1:8085");
      wsRef.current = ws;

      ws.onopen = () => {
        setHelperStatus("Helper Connected");
        toast.success("RFID helper connected");
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "status") {
            const message = normalizeText(data.message || "Updated");
            setReaderStatus(message || "Updated");

            const lower = message.toLowerCase();

            if (
              lower.includes("init") ||
              lower.includes("ready") ||
              lower.includes("initialized")
            ) {
              setIsReaderInitialized(true);
            }

            if (lower.includes("scan started") || lower.includes("scanning")) {
              setIsScanning(true);
            }

            if (
              lower.includes("stop") ||
              lower.includes("stopped") ||
              lower.includes("released") ||
              lower.includes("free")
            ) {
              setIsScanning(false);
            }
          }

          if (data.type === "sync_progress") {
            setSyncProgress({
              syncing: true,
              total: Number(data.total || 0),
              synced: Number(data.synced || 0),
              percent: Number(data.percent || 0),
            });

            setReaderStatus(`Syncing stock ${Number(data.percent || 0)}%`);
          }

          if (data.type === "sync_complete") {
            setSyncProgress({
              syncing: false,
              total: Number(data.total || 0),
              synced: Number(data.synced || 0),
              percent: 100,
            });

            setIsExpectedLoaded(true);
            setReaderStatus("Expected stock synced");
            toast.success(`Stock synced: ${data.synced || 0}`);

            setTimeout(() => {
              requestStockList({
                status: "not_found",
                page: 1,
                query: "",
                append: false,
              });
            }, 200);
          }

          if (data.type === "sync_error") {
            setSyncProgress((prev) => ({
              ...prev,
              syncing: false,
            }));

            setIsExpectedLoaded(false);
            setReaderStatus("Stock sync failed");
            toast.error(data.message || "Stock sync failed");
          }

          if (data.type === "summary") {
            setHelperSummary(data);

            if (typeof data.findMode === "boolean") {
              setIsFindMode(data.findMode);
            }

            if (data.targetFindEpc !== undefined) {
              const epc = normalizeUpper(data.targetFindEpc || "");
              setTargetFindEpc(epc);

              if (!epc && !data.findMode) {
                resetFindUi();
              }
            }

            if (data.lastFindLevel !== undefined) {
              setFindLevel(normalizeText(data.lastFindLevel || ""));
            }

            if (data.lastFindStrengthPercent !== undefined) {
              setFindStrength(Number(data.lastFindStrengthPercent) || 0);
            }
          }

          if (data.type === "find_mode") {
            const epc = normalizeUpper(data.epc || "");

            if (data.status === "started") {
              setIsFindMode(true);
              setTargetFindEpc(epc);
              setFindStatus("searching");
              setFindMessage("Searching for selected item");
              setFoundAt("");
            }

            if (data.status === "stopped") {
              resetFindUi();
            }
          }

          if (data.type === "find_progress") {
            const epc = normalizeUpper(data.epc || "");
            if (!epc) return;

            const strength = Math.max(
              0,
              Math.min(100, Number(data.strengthPercent) || 0),
            );

            setLastEpc(epc);
            setIsFindMode(true);
            setTargetFindEpc(epc);
            setFindStatus("searching");
            setFindLevel(normalizeText(data.level || ""));
            setFindStrength(strength);
            setFindMessage(
              getSignalMessage(strength, normalizeText(data.message || "")),
            );
            setFoundAt(data.scannedAt || new Date().toLocaleTimeString());
          }

          if (data.type === "list_results") {
            const items = Array.isArray(data.data) ? data.data : [];

            const normalizedRows = items
              .map((item) => ({
                id: item?._id || item?.epcCode,
                itemCode: item?.itemCode || "",
                rfidCode: item?.rfidCode || "",
                epcCode: normalizeUpper(item?.epcCode || ""),
                categoryName: item?.categoryName || item?.CategoryName || "",
                productName: item?.productName || item?.ProductName || "",
                designName: item?.designName || item?.DesignName || "",
                purityName: item?.purityName || item?.PurityName || "",
                branchName: item?.branchName || item?.BranchName || "",
                counterName: item?.counterName || item?.CounterName || "",
                status: item?.status || "not_found",
                source: "SQLITE_LIST",
                scannedAt: "",
              }))
              .filter((x) => x.epcCode);

            setRows((prev) => {
              if (Number(data.page || 1) > 1) {
                const existing = new Set(
                  prev.map((x) => normalizeUpper(x.epcCode)),
                );
                const merged = [...prev];

                normalizedRows.forEach((row) => {
                  if (!existing.has(normalizeUpper(row.epcCode))) {
                    merged.push(row);
                  }
                });

                return merged;
              }

              return normalizedRows;
            });

            setListPage(Number(data.page || 1));
            setListTotal(Number(data.total || 0));
            setListHasMore(!!data.hasMore);
            setListLoading(false);

            if (data.error) {
              toast.error(data.error);
            }
          }

          if (data.type === "search_results") {
            const items = Array.isArray(data.data) ? data.data : [];

            const normalizedRows = items
              .map((item) => ({
                id: item?._id || item?.epcCode,
                itemCode: item?.itemCode || "",
                rfidCode: item?.rfidCode || "",
                epcCode: normalizeUpper(item?.epcCode || ""),
                categoryName: item?.categoryName || item?.CategoryName || "",
                productName: item?.productName || item?.ProductName || "",
                designName: item?.designName || item?.DesignName || "",
                purityName: item?.purityName || item?.PurityName || "",
                branchName: item?.branchName || item?.BranchName || "",
                counterName: item?.counterName || item?.CounterName || "",
                status: item?.status || "not_found",
                source: "SQLITE_SEARCH",
                scannedAt: "",
              }))
              .filter((x) => x.epcCode);

            setRows(normalizedRows);

            if (data.error) {
              toast.error(data.error);
            }
          }

          if (data.type === "scan_result") {
            const epc = normalizeUpper(data.epc || data.epcCode || "");
            if (!epc) return;

            setLastEpc(epc);

            if (data.status === "duplicate") return;
            if (isFindModeRef.current) return;

            setRows((prev) => {
              const idx = prev.findIndex(
                (x) => normalizeUpper(x.epcCode) === epc,
              );

              if (idx === -1) return prev;

              const updated = [...prev];
              updated[idx] = {
                ...updated[idx],
                status: "matched",
                scannedAt: data.scannedAt || new Date().toLocaleTimeString(),
                source: data.source || "RFID",
              };

              return updated;
            });
          }
        } catch (err) {
          console.error("WS parse error", err);
        }
      };

      ws.onclose = () => {
        setHelperStatus("Helper Disconnected");
        setIsScanning(false);
      };

      ws.onerror = () => {
        setHelperStatus("Connection Failed");
        setIsScanning(false);
      };
    } catch (err) {
      console.error("WS connection error", err);
      setHelperStatus("Connection Failed");
      setIsScanning(false);
    }
  };

  const sendRaw = (payload) => {
    if (!(wsRef.current && wsRef.current.readyState === WebSocket.OPEN)) {
      toast.error("Helper app not connected");
      return false;
    }
    wsRef.current.send(payload);
    return true;
  };

  const requestStockList = ({
    status = statusFilter,
    page = 1,
    query = search.trim(),
    append = false,
  } = {}) => {
    if (!isExpectedLoaded) return false;

    const ok = sendRaw(
      JSON.stringify({
        type: "list_expected_stock",
        status,
        query,
        page,
        pageSize: 100,
      }),
    );

    if (!ok) return false;

    setListLoading(true);
    if (!append) setRows([]);
    return true;
  };

  const handleInitReader = () => {
    const ok = sendRaw("init_reader");
    if (!ok) return;
    setReaderStatus("Initializing Reader...");
    setIsReaderInitialized(true);
    toast.success("Reader init command sent");
  };

  const handleStartScan = () => {
    if (!isReaderInitialized) {
      toast.error("Please init reader first");
      return;
    }

    if (!isExpectedLoaded) {
      toast.error("Please load expected stock first");
      return;
    }

    const ok = sendRaw("start_scan");
    if (!ok) return;

    setReaderStatus("Scanning...");
    setIsScanning(true);
    toast.success("Scanning started");
  };

  const handleStopScan = () => {
    const ok = sendRaw("stop_scan");
    if (!ok) return;

    setReaderStatus("Scan Stopped");
    setIsScanning(false);
    toast.success("Scanning stopped");
  };

  const syncExpectedStockOnAndroid = async () => {
    try {
      const tokenRes = await getHelperSyncToken().unwrap();
      const token = tokenRes?.token || "";

      if (!token) {
        toast.error("Helper sync token not found");
        return;
      }

      const ok = sendRaw(
        JSON.stringify({
          type: "sync_expected_stock_from_server",
          baseUrl: process.env.REACT_APP_API_BASE_URL,
          token,
          pageSize: 10000,
          branchIds: filters.branchIds.map((x) => x.value),
          counterIds: filters.counterIds.map((x) => x.value),
          categoryIds: filters.categoryIds.map((x) => x.value),
          productIds: filters.productIds.map((x) => x.value),
          designIds: filters.designIds.map((x) => x.value),
          purityIds: filters.purityIds.map((x) => x.value),
        }),
      );

      if (!ok) return;

      setShowSyncModal(false);
      setRows([]);
      setLastEpc("");
      setIsExpectedLoaded(false);
      setStatusFilter("not_found");
      resetFindUi();
      setSelectedEpcs([]);
      setFindQueue([]);

      setSyncProgress({
        syncing: true,
        total: 0,
        synced: 0,
        percent: 0,
      });

      setReaderStatus("Stock sync started...");
      toast.success("Stock sync started on helper");
    } catch (err) {
      console.error("Helper sync token error", err);
      toast.error(
        err?.data?.msg || err?.message || "Unable to start stock sync",
      );
    }
  };

  useEffect(() => {
    if (!isExpectedLoaded) return;

    const timer = setTimeout(() => {
      requestStockList({
        status: statusFilter,
        page: 1,
        query: search.trim(),
        append: false,
      });
    }, 350);

    return () => clearTimeout(timer);
  }, [search, statusFilter, isExpectedLoaded]);

  const addManualEpc = () => {
    const epc = normalizeUpper(manualEpc);

    if (!epc) {
      toast.error("Please enter EPC");
      return;
    }

    const ok = sendRaw(JSON.stringify({ type: "manual_epc", epc }));
    if (!ok) return;

    setManualEpc("");
    setShowManualModal(false);
    toast.success("Manual EPC sent");
  };

  const startFindItem = (item, fromQueue = false) => {
    const epc = normalizeUpper(item?.epcCode);
    if (!epc) {
      toast.error("EPC not available for this item");
      return;
    }

    if (!isReaderInitialized) {
      toast.error("Please init reader first");
      return;
    }

    if (!isExpectedLoaded) {
      toast.error("Please load expected stock first");
      return;
    }

    if (isScanning) {
      sendRaw("stop_scan");
      setIsScanning(false);
      setReaderStatus("Scan Stopped");
    }

    const ok = sendRaw(JSON.stringify({ type: "find_epc", epc }));
    if (!ok) return;

    setTargetFindItem(item);
    setIsFindMode(true);
    setTargetFindEpc(epc);
    setFindStatus("searching");
    setFindMessage("Searching for selected item");
    setFindLevel("searching");
    setFindStrength(0);
    setFoundAt("");

    if (!fromQueue) {
      toast.success("Search started");
    }
  };

  const stopFindItem = () => {
    const ok = sendRaw(JSON.stringify({ type: "stop_find_epc" }));
    if (!ok) return;

    resetFindUi();
    toast.success("Find mode stopped");
  };

  const rowsByEpc = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const epc = normalizeUpper(row.epcCode);
      if (epc) map.set(epc, row);
    });
    return map;
  }, [rows]);

  const handleConfirmFound = () => {
    const epc = normalizeUpper(targetFindEpc);
    if (!epc) {
      toast.error("No active target");
      return;
    }

    setRows((prev) =>
      prev.map((row) =>
        normalizeUpper(row.epcCode) === epc
          ? {
              ...row,
              status: "matched",
              scannedAt: foundAt || new Date().toLocaleTimeString(),
              source: "RFID",
            }
          : row,
      ),
    );

    const remaining = findQueue.filter((x) => normalizeUpper(x) !== epc);
    setSelectedEpcs((prev) => prev.filter((x) => normalizeUpper(x) !== epc));
    setFindQueue(remaining);

    toast.success("Item marked as found");

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop_find_epc" }));
    }

    if (remaining.length > 0) {
      const nextItem = rowsByEpc.get(normalizeUpper(remaining[0] || ""));
      if (nextItem) {
        setTimeout(() => {
          startFindItem(nextItem, true);
        }, 500);
        return;
      }
    }

    resetFindUi();
    setShowFindModal(false);
  };

  const clearAll = () => {
    setRows((prev) =>
      prev.map((x) => ({
        ...x,
        status: "not_found",
        scannedAt: "",
        source: "EXPECTED",
      })),
    );

    setLastEpc("");
    setIsScanning(false);
    setReaderStatus(isReaderInitialized ? "Ready To Scan" : "Not Initialized");
    resetFindUi();
    setHelperSummary(null);
    setStatusFilter("not_found");
    setSelectedEpcs([]);
    setFindQueue([]);
    resetFilters();

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "clear_session" }));
      wsRef.current.send(JSON.stringify({ type: "stop_find_epc" }));
    }

    toast.success("Session reset");
  };

  const handleFindSelected = () => {
    if (!selectedEpcs.length) {
      toast.error("Select at least one missing item");
      return;
    }

    const queue = [...selectedEpcs];
    setFindQueue(queue);

    const first = rowsByEpc.get(normalizeUpper(queue[0]));
    if (!first) {
      toast.error("Selected item not found");
      return;
    }

    startFindItem(first);
  };

  const summary = useMemo(() => {
    const expected =
      Number(helperSummary?.expectedCount || 0) ||
      Number(syncProgress.total || 0);
    const matched = Number(helperSummary?.matchedCount || 0);
    const unmatched =
      Number(helperSummary?.missingCount || 0) ||
      Math.max(expected - matched, 0);

    return { expected, matched, unmatched };
  }, [helperSummary, syncProgress]);

  const filteredRows = useMemo(() => {
    let data = [...rows];

    if (statusFilter) {
      data = data.filter((x) => x.status === statusFilter);
    }

    data.sort((a, b) => {
      const order = { not_found: 0, matched: 1 };
      return (order[a.status] ?? 9) - (order[b.status] ?? 9);
    });

    return data;
  }, [rows, statusFilter]);

  const helperBadgeClass =
    helperStatus === "Helper Connected"
      ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
      : helperStatus === "Connecting..."
        ? "bg-amber-100 text-amber-700 border border-amber-200"
        : "bg-rose-100 text-rose-700 border border-rose-200";

  const readerBadgeClass = isScanning
    ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
    : isReaderInitialized
      ? "bg-blue-100 text-blue-700 border border-blue-200"
      : "bg-slate-100 text-slate-700 border border-slate-200";

  const activeFilterCount =
    countSelected(filters.branchIds) +
    countSelected(filters.counterIds) +
    countSelected(filters.categoryIds) +
    countSelected(filters.productIds) +
    countSelected(filters.designIds) +
    countSelected(filters.purityIds);

  const renderStockRow = ({ index, rowIndex, style, rows }) => {
    const actualIndex = index ?? rowIndex;
    const item = rows?.[actualIndex];
    if (!item) return null;

    const epc = normalizeUpper(item.epcCode);
    const isCurrentFinding =
      isFindMode && normalizeUpper(targetFindEpc) === epc;

    return (
      <div
        style={{
          ...style,
          paddingLeft: 6,
          paddingRight: 6,
          paddingTop: 2,
          paddingBottom: 10,
        }}
      >
        <div
          key={item.id || item.epcCode}
          className={`rounded-2xl border bg-white px-4 py-3.5 shadow-sm hover:shadow-md transition-shadow duration-200 ${
            isCurrentFinding
              ? "border-violet-300 bg-violet-50/60"
              : item.status === "matched"
                ? "border-emerald-200 bg-emerald-50/40"
                : ""
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-extrabold border ${toStatusClass(
                    item.status,
                  )}`}
                >
                  {item.status === "matched" ? "MATCHED" : "MISSING"}
                </div>

                {isCurrentFinding ? (
                  <div className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-extrabold border bg-violet-100 text-violet-700 border-violet-200">
                    TRACKING
                  </div>
                ) : null}

                <div className="text-[12px] font-extrabold text-slate-800 break-words">
                  {item.itemCode || "-"}
                </div>
              </div>

              <div className="text-[10px] text-slate-500 mt-1 leading-4">
                {[
                  item.branchName,
                  item.counterName,
                  item.productName,
                  item.categoryName,
                  item.designName,
                  item.purityName,
                ]
                  .filter(Boolean)
                  .join(" · ") || "-"}
              </div>

              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[10px] text-slate-600">
                <div>
                  <span className="font-semibold text-slate-500">RFID:</span>{" "}
                  {item.rfidCode || "-"}
                </div>

                <div>
                  <span className="font-semibold text-slate-500">Time:</span>{" "}
                  {item.scannedAt || "-"}
                </div>

                <div className="sm:col-span-1 break-all leading-4">
                  <span className="font-semibold text-slate-500">EPC:</span>{" "}
                  {item.epcCode}
                </div>
              </div>
            </div>

            <div className="shrink-0">
              {item.status === "not_found" ? (
                <button
                  onClick={() => {
                    setSelectedEpcs([epc]);
                    setFindQueue([epc]);
                    const itemToFind = rowsByEpc.get(epc);

                    if (itemToFind) {
                      startFindItem(itemToFind);
                    }

                    setShowFindModal(true);
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                    isCurrentFinding
                      ? "border-violet-300 bg-violet-100 text-violet-700"
                      : "border-violet-200 bg-white text-[#6b59c6] hover:bg-violet-50"
                  }`}
                >
                  <FaCrosshairs className="text-[11px]" />
                  {isCurrentFinding ? "Finding..." : "Find"}
                </button>
              ) : (
                <div className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold border-emerald-200 bg-emerald-50 text-emerald-700">
                  <FaCheckCircle className="text-[11px]" />
                  Found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <ManualEpcModal
        open={showManualModal}
        onClose={() => setShowManualModal(false)}
        manualEpc={manualEpc}
        setManualEpc={setManualEpc}
        onAddManualEpc={addManualEpc}
      />

      <AnimatePresence>
        {showFindModal && (
          <FindItemsModal
            open={showFindModal}
            onClose={() => setShowFindModal(false)}
            selectedCount={selectedEpcs.length}
            targetFindEpc={targetFindEpc}
            findStatus={findStatus}
            findMessage={findMessage}
            findLevel={findLevel}
            findStrength={findStrength}
            foundAt={foundAt}
            remainingQueue={findQueue.length}
            isFindMode={isFindMode}
            stopFindItem={stopFindItem}
            onConfirmFound={handleConfirmFound}
            handleFindSelected={handleFindSelected}
          />
        )}
      </AnimatePresence>

      <SyncFilterModal
        open={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        onReset={resetFilters}
        onSync={syncExpectedStockOnAndroid}
        syncing={syncProgress.syncing}
        filters={filters}
        branchOptions={branchOptions}
        counterOptions={counterOptions}
        categoryOptions={categoryOptions}
        productOptions={productOptions}
        designOptions={designOptions}
        purityOptions={purityOptions}
        selectedCategoryValues={selectedCategoryValues}
        selectedProductValues={selectedProductValues}
        selectedDesignValues={selectedDesignValues}
        selectedPurityValues={selectedPurityValues}
        onBranchChange={onBranchChange}
        onCounterChange={onCounterChange}
        onCategoryChange={onCategoryChange}
        onProductChange={onProductChange}
        onDesignChange={onDesignChange}
        onPurityChange={onPurityChange}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-3 lg:gap-4">
        <motion.div
          variants={fadeUp(0)}
          initial="hidden"
          animate="visible"
          className="rounded-2xl bg-white shadow-lg border overflow-hidden lg:sticky lg:self-start"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#9481ce] text-white">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/10 border border-white/15">
                <FaBoxOpen className="text-white" />
              </span>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-base font-extrabold truncate">
                  Stock Validation
                </h1>
                <p className="text-[11px] text-violet-100/90 truncate">
                  Validate & Find Missing Items
                </p>
              </div>
            </div>

            <button
              onClick={connectHelper}
              className="group inline-flex items-center gap-2 rounded-full bg-white text-[#6b4fd8] px-3.5 py-2 text-[11px] font-semibold shadow-sm hover:bg-violet-50 transition"
              title="Reconnect"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#6b59c6]/10">
                <FiRotateCcw className="text-[12px] text-[#6b59c6]" />
              </span>
              <span className="hidden sm:inline">Reconnect</span>
            </button>
          </div>

          <div className="p-3 sm:p-4 bg-white">
            <div className="grid grid-cols-1 gap-3">
              <div className="flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-semibold ${helperBadgeClass}`}
                >
                  <FaWifi className="text-[9px]" />
                  {helperStatus}
                </span>

                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-semibold ${readerBadgeClass}`}
                >
                  <FaInfoCircle className="text-[9px]" />
                  {readerStatus}
                </span>
              </div>

              {lastEpc ? (
                <div className="rounded-2xl border bg-slate-50 px-3 py-2">
                  <div className="text-[10px] font-semibold text-slate-500">
                    Last EPC
                  </div>
                  <div className="mt-1 text-[11px] font-semibold text-slate-800 break-all">
                    {lastEpc}
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-2">
                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={handleInitReader}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#6b59c6] px-4 py-2.5 text-[11px] font-semibold text-white shadow-sm hover:opacity-95"
                >
                  <FaPlug />
                  Init Reader
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={() => setShowSyncModal(true)}
                  disabled={syncProgress.syncing}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-[#6b59c6] px-4 py-2.5 text-[11px] font-semibold border border-violet-200 shadow-sm hover:bg-violet-50 disabled:opacity-50"
                >
                  {syncProgress.syncing ? <Spinner size={14} /> : <FaBoxOpen />}
                  {syncProgress.syncing
                    ? `${syncProgress.percent}%`
                    : "Sync Stock"}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={isScanning ? handleStopScan : handleStartScan}
                  disabled={
                    !isScanning &&
                    (!isReaderInitialized || !isExpectedLoaded || isFindMode)
                  }
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-[11px] font-semibold text-white shadow-sm disabled:opacity-50 ${
                    isScanning
                      ? "bg-amber-500 hover:bg-amber-600"
                      : "bg-emerald-500 hover:bg-emerald-600"
                  }`}
                >
                  {isScanning ? <FaStop /> : <FaPlay />}
                  {isScanning ? "Stop Scan" : "Start Scan"}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98, y: 0 }}
                  type="button"
                  onClick={clearAll}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-[11px] font-semibold border shadow-sm bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                >
                  <FaTrashAlt />
                  Reset
                </motion.button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setShowManualModal(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <FaPlus />
                  Add EPC
                </button>

                <button
                  onClick={() => setShowFindModal(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-2.5 text-[11px] font-semibold text-white hover:bg-violet-700 shadow-sm"
                >
                  <FaCrosshairs />
                  Open Find
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <SummaryChip
                  title="Expected"
                  value={summary.expected}
                  tone="slate"
                  icon={<FaBoxOpen />}
                />
                <SummaryChip
                  title="Matched"
                  value={summary.matched}
                  tone="emerald"
                  icon={<FaCheckCircle />}
                />
                <SummaryChip
                  title="Missing"
                  value={summary.unmatched}
                  tone="rose"
                  icon={<FaExclamationTriangle />}
                />
              </div>

              <div className="rounded-2xl border bg-slate-50 px-3 py-2">
                <div className="text-[10px] font-semibold text-slate-500">
                  Sync Filters Selected
                </div>
                <div className="mt-1 text-[12px] font-extrabold text-violet-700">
                  {activeFilterCount}
                </div>
              </div>

              <div className="rounded-2xl border bg-gradient-to-b from-slate-50 to-white px-3 py-2">
                <div className="text-[12px] font-extrabold text-slate-800 truncate">
                  Helper APK
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">
                  Reader bridge app for RFID gun
                </div>

                <a
                  href={helperAppUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#6b4fd8] px-3.5 py-2 text-[11px] font-semibold shadow-sm border border-violet-200 hover:bg-violet-50"
                >
                  <FaMobileAlt />
                  Download Helper
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp(0.1)}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-2xl border shadow-sm overflow-hidden min-w-0"
        >
          <div className="px-4 py-3 border-b bg-gradient-to-b from-slate-50 to-white">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                  <span>Stock Items</span>
                  <span className="text-[11px] text-slate-500 font-semibold">
                    ({filteredRows.length}/{listTotal || filteredRows.length})
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Expected items with live matched / missing status
                </div>
              </div>

              <div className="flex items-center gap-2">
                <ListLoadingPill show={syncProgress.syncing} />
              </div>
            </div>
          </div>

          <div className="p-3 sm:p-4">
            <div className="grid grid-cols-[1fr,110px] gap-2 mb-3">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[11px]" />
                <input
                  type="text"
                  placeholder="Search item / RFID / EPC"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-3 rounded-2xl text-[12px] text-slate-800 border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setStatusFilter(value);
                  requestStockList({
                    status: value,
                    page: 1,
                    query: search.trim(),
                    append: false,
                  });
                }}
                className="px-3 py-3 rounded-2xl text-[12px] text-slate-800 border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300"
              >
                <option value="not_found">Missing</option>
                <option value="">All</option>
                <option value="matched">Matched</option>
              </select>
            </div>

            {!isExpectedLoaded ? (
              <EmptyState
                icon={<FaBoxOpen className="text-[22px]" />}
                title={
                  syncProgress.syncing ? "Syncing stock" : "Sync stock first"
                }
                subtitle={
                  syncProgress.syncing
                    ? `${syncProgress.synced} / ${syncProgress.total} synced`
                    : "Sync Stock dabao, filter modal khulega, jo selected hoga wahi sync jayega."
                }
              />
            ) : !filteredRows.length ? (
              <EmptyState
                icon={<FaExclamationTriangle className="text-[22px]" />}
                title={
                  search.trim()
                    ? "No matching item found"
                    : "Search item to find"
                }
                subtitle={
                  search.trim()
                    ? "Android local database me is search ka result nahi mila."
                    : "ItemCode / RFID / EPC search karo, fir Find button se radar start hoga."
                }
              />
            ) : (
              <div className="h-[calc(100vh-210px)] md:h-[calc(100vh-220px)] pr-1 [contain:content]">
                <VirtualList
                  style={{ height: listHeight, width: "100%" }}
                  rowCount={filteredRows.length}
                  rowHeight={148}
                  rowComponent={renderStockRow}
                  rowProps={{ rows: filteredRows }}
                  overscanCount={10}
                />

                {listHasMore ? (
                  <div className="pt-3 flex justify-center">
                    <button
                      type="button"
                      disabled={listLoading}
                      onClick={() =>
                        requestStockList({
                          status: statusFilter,
                          page: listPage + 1,
                          query: search.trim(),
                          append: true,
                        })
                      }
                      className="inline-flex items-center justify-center rounded-full border border-violet-200 bg-white px-4 py-2 text-[11px] font-semibold text-[#6b59c6] hover:bg-violet-50 disabled:opacity-50"
                    >
                      {listLoading ? "Loading..." : "Load More"}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
